import CustomButton from "@components/common/Button";
import LoadingSpinner from "@components/common/LoadingSpinner";
import NotFound from "@components/common/NotFound";
import { notify } from "@components/common/Notification";
import { CustomImageUpload } from "@components/CustomImageUpload";
import TenantProfileService from "@/services/APIs/TenantProfileService";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Checkbox, Form, Input } from "antd";
import { Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// const DEFAULT_AVATAR_URL = "https://i.pravatar.cc/300?img=12";
// const DEFAULT_LOGO_URL = "http://djangoproject.universal-software.in/media/upload/jenilKalariya/images/image_F8X4JLF.png";

// normalize API response to a single record or null
const pickOne = (res: any) => {
  if (!res) {
    return null;
  }
  if (Array.isArray(res)) {
    return res[0] ?? null;
  }
  if (Array.isArray(res.results)) {
    return res.results?.[0] ?? null; // <-- note ?. before [0]
  }
  return res; // assume it's already a single record
};

const deepEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

const TenantProfilePage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [logo, setLogo] = useState<string | null>(null);
  const [favicon, setFavicon] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const initialRef = useRef<any>(null); // snapshot of initial values for diff

  const {
    data: tenantProfile,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["tenantProfile"],
    queryFn: () => TenantProfileService.listTenantProfiles(),
  });

  const profile = pickOne(tenantProfile);

  console.log(profile, "profile");

  // Seed form + snapshot for diff
  useEffect(() => {
    if (profile) {
      const initForForm = { profile };
      form.setFieldsValue({
        ...initForForm,
        logo: profile.logo,
        favicon: profile.favicon,
      });

      // for change-detection baseline, store normalized strings
      initialRef.current = {
        ...initForForm,
        logo: profile.logo,
        favicon: profile.favicon,
      };
      setHasChanges(false);
    }
  }, [profile, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      logo: logo,
      favicon: favicon,
    };

    // If editing and nothing changed, just notify
    if (deepEqual(payload, initialRef.current)) {
      notify.info("No changes", "There are no changes to save.");
      return;
    }

    setLoading(true);
    try {
      if (profile) {
        await TenantProfileService.createTenantProfile(payload); // <-- FIXED: update on edit
        notify.success("Tenant Profile", "Changes have been saved.");
        initialRef.current = payload; // refresh baseline
        setHasChanges(false);
        await refetch(); // optional: refresh from server
      } else {
        const response =
          await TenantProfileService.createTenantProfile(payload);
        if (response) {
          notify.success("Tenant Profile", "Tenant profile has been saved.");
          navigate(navigationRoutes.TENANT_PROFILE);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (isError) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={"Add Tenant Profile"}
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={loading}
              // optionally disable when no changes in edit mode
              disabled={loading || !hasChanges}
              title={"Save New Tenant Profile"}
            >
              {loading ? "Saving..." : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form
        form={form}
        layout="vertical"
        // track changes to enable/disable save
        onValuesChange={(_, all) =>
          setHasChanges(!deepEqual(all, initialRef.current))
        }
      >
        {/* Tenant details (above images) */}
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter tenant name" }]}
          >
            <Input placeholder="e.g., NT" />
          </Form.Item>

          <Form.Item name="app_name" label="App name">
            <Input placeholder="Optional app display name" />
          </Form.Item>

          <div className="col-span-2 flex items-center gap-6">
            <Form.Item name="is_active" valuePropName="checked" className="m-0">
              <Checkbox>Is active</Checkbox>
            </Form.Item>
          </div>

          {/* Store strings with valueFormat so API gets 'YYYY-MM-DD' / 'HH:mm' */}
          <Form.Item name="date_format" label="Date" className="w-full">
            <Input placeholder="Optional date format" />
          </Form.Item>

          <Form.Item name="time_format" label="Time" className="w-full">
            <Input placeholder="Optional time format" />
          </Form.Item>

          <Form.Item name="login_with_mobile_otp" valuePropName="checked">
            <Checkbox>Login with mobile otp</Checkbox>
          </Form.Item>

          <Form.Item name="disable_signups" valuePropName="checked">
            <Checkbox>Disable signups</Checkbox>
          </Form.Item>
        </div>

        {/* Branding (images) */}
        <div className="mt-6 grid grid-cols-2 gap-8">
          <Form.Item
            name="logo"
            label="Logo"
            valuePropName="value"
            className="w-full"
          >
            <CustomImageUpload
              shape="square"
              borderStyle="dashed"
              boxWidth={800}
              boxHeight={200}
              accept="image/*"
              className="w-full"
              initialUrl={logo as string}
              disabled={loading || isLoading}
              onUploaded={response => {
                const filePath = response[0].file_path;
                console.log(filePath, "filePath");
                setLogo(filePath); // Update the logo state
              }}
            />
          </Form.Item>

          <Form.Item
            name="favicon"
            label="Favicon"
            valuePropName="value"
            className="w-full"
          >
            <CustomImageUpload
              shape="square"
              borderStyle="dashed"
              accept="image/*"
              className="w-full"
              boxWidth={800}
              boxHeight={200}
              initialUrl={favicon as string}
              onUploaded={response => {
                const filePath = response[0].file_path;
                setFavicon(filePath);
              }}
              disabled={loading || isLoading}
            />
          </Form.Item>
        </div>
      </Form>
    </Card>
  );
};

export default TenantProfilePage;
